// İSG Portal — JWT payload çözümü (yalnız istemci-tarafı okuma; DOĞRULAMA DEĞİL).
//
// Access token imza doğrulaması **sunucunun** işidir (api-contract §5.1). Burada
// token yalnızca principal snapshot'ı (ad/rol/culture) çıkarmak için base64url
// decode edilir — güven kararı buna dayanmaz. Claim isimleri api-contract §5.1
// tablosundan birebir alınmıştır.

import type { Principal, PrincipalType } from './types';

interface JwtClaims {
  principalType?: string;
  userId?: number | string;
  supplierUserId?: number | string;
  uuid?: string;
  companyId?: number | string;
  email?: string;
  culture?: string;
  timezone?: string;
  /** Çoklu claim; JWT'de dizi veya tek değer olarak gelebilir. */
  roleUuid?: string | string[];
  exp?: number;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const withPad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
  const binary = atob(withPad);
  // UTF-8 çöz (Türkçe/Almanca karakterler claim'de olabilir).
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** JWT payload'ını ayrıştırır; bozuk/eksik token'da `null`. */
export function decodeJwtClaims(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) {
    return null;
  }
  try {
    return JSON.parse(base64UrlDecode(parts[1])) as JwtClaims;
  } catch {
    return null;
  }
}

function asNumber(value: number | string | undefined): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function asRoleUuids(value: string | string[] | undefined): string[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value.filter(Boolean) : [value];
}

/** Access token'dan principal snapshot'ı üretir; çözülemezse `null`. */
export function principalFromToken(accessToken: string): Principal | null {
  const claims = decodeJwtClaims(accessToken);
  if (!claims?.principalType) {
    return null;
  }
  return {
    principalType: claims.principalType as PrincipalType,
    uuid: claims.uuid ?? null,
    userId: asNumber(claims.userId),
    supplierUserId: asNumber(claims.supplierUserId),
    companyId: asNumber(claims.companyId),
    email: claims.email ?? null,
    culture: claims.culture ?? null,
    timezone: claims.timezone ?? null,
    roleUuids: asRoleUuids(claims.roleUuid),
  };
}

/** Token `exp`'inin geçmiş olup olmadığı (saniye epoch); yoksa `false`. */
export function isTokenExpired(accessToken: string, skewSeconds = 0): boolean {
  const claims = decodeJwtClaims(accessToken);
  if (!claims?.exp) {
    return false;
  }
  return claims.exp * 1000 <= Date.now() + skewSeconds * 1000;
}
